import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, onSignInExpired, signInStarted } from '../lib/api';
import { getBrowserTz } from '../lib/timezone';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const data = await api.get('/auth/me');
      setUser(data.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // The API client renews an expired access token by itself. This fires only when renewing
  // failed too, so the sign-in really is over and the app must stop showing them as signed in.
  useEffect(() => onSignInExpired(() => setUser(null)), []);

  const login = async (email, password) => {
    const data = await api.post('/auth/login', { email, password });
    if (data.mfaRequired) {
      return { mfaRequired: true, mfaToken: data.mfaToken };
    }
    signInStarted();
    setUser(data.user);
    return { user: data.user };
  };

  const loginMfa = async (mfaToken, code) => {
    const data = await api.post('/auth/login/mfa', { mfaToken, code });
    signInStarted();
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name, role = 'student') => {
    // Send the detected zone with the signup itself: without it the new account falls back to
    // the schema default (Asia/Manila) and every session time is wrong until the user happens
    // to find the timezone setting. Attached here rather than in each caller so every
    // registration path gets it.
    const data = await api.post('/auth/register', {
      email, password, name, role, timezone: getBrowserTz(),
    });
    return data;
  };

  const resendVerification = async (email) => {
    return api.post('/auth/resend-verification', { email });
  };

  const logout = async () => {
    await api.post('/auth/logout');
    setUser(null);
  };

  const updateProfile = async (fields) => {
    const data = await api.put('/profile', fields);
    setUser(data.user);
    return data.user;
  };

  const setupMfa = async () => {
    return api.post('/auth/mfa/setup');
  };

  const enableMfa = async (code) => {
    const data = await api.post('/auth/mfa/enable', { code });
    await checkAuth();
    return data;
  };

  const disableMfa = async (code, password) => {
    const data = await api.post('/auth/mfa/disable', { code, password });
    await checkAuth();
    return data;
  };

  const verifyEmail = async (token) => {
    return api.get('/auth/verify-email?token=' + encodeURIComponent(token));
  };

  const forgotPassword = async (email) => {
    return api.post('/auth/forgot-password', { email });
  };

  const setPassword = async (token, password) => {
    return api.post('/auth/set-password', { token, password });
  };

  const isAuthenticated = !!user;
  const isStudent = user?.role === 'student';
  const isInstructor = user?.role === 'instructor';
  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        isStudent,
        isInstructor,
        isAdmin,
        login,
        loginMfa,
        register,
        resendVerification,
        logout,
        updateProfile,
        setupMfa,
        enableMfa,
        disableMfa,
        verifyEmail,
        forgotPassword,
        setPassword,
        refresh: checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}

// Same context, but null instead of throwing when there's no provider — for helpers that
// only *enrich* a component when someone is signed in (e.g. useViewerTz picking the saved
// timezone). Lets those components still render standalone, as the unit tests do.
export function useOptionalAuth() {
  return useContext(AuthContext);
}
